import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Box, ClickAwayListener, Paper, Popper } from '@mui/material';
import { MacroEditorProps, MacroSuggestion } from './types';
import { MacroSuggestions } from './components';
import {
  extractPlainText,
  parseAndRenderContent,
  getCursorPosition,
  findMacroRangeAtCursor,
  buildRangeFromOffsets,
  wrapRangeWithMacroSpan,
  replaceRangeWithTextAndPlaceCursor,
  isValidMacro,
  findMacroSpanAtCursor,
} from './utils';
import styles from './MacroEditor.module.scss';

export const MacroEditor: React.FC<MacroEditorProps> = ({
  value,
  onChange,
  suggestions,
  placeholder = '',
  clearPlaceholderOnFocus = false,
  multiline = false,
  rows = 1,
  maxRows,
  sx = {},
  isJson = false,
  renderSuggestionFooter,
  renderMacroPopup,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [triggerPosition, setTriggerPosition] = useState<number>(-1);
  const [searchText, setSearchText] = useState('');
  const [editingMacro, setEditingMacro] = useState<string | null>(null);
  const isUpdatingRef = useRef(false);
  // Last plain-text value handleInput emitted via onChange. The value-sync effect
  // treats `value === lastEmittedRef.current` as the echo of our own keystroke.
  const lastEmittedRef = useRef<string | null>(null);
  const isComposingRef = useRef(false);
  const prevSuggestionsRef = useRef(suggestions);
  const isEditingMacroRef = useRef(false);

  // Memoize filtered suggestions
  const filteredSuggestions = useMemo(() => {
    if (!searchText) return suggestions;

    const lowerSearch = searchText.toLowerCase();
    return suggestions.filter(
      (s) =>
        s.display_name.toLowerCase().includes(lowerSearch) ||
        s.name.toLowerCase().includes(lowerSearch),
    );
  }, [suggestions, searchText]);

  // Memoize parsed content to avoid re-parsing on every render
  const parsedContent = useMemo(() => {
    return parseAndRenderContent(value || '', suggestions) || '<br>';
  }, [value, suggestions]);

  // Mount once: render initial content (parse into spans if any existing macros)
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = parsedContent;
    }
  }, []);

  useEffect(() => {
    // Echo of our own keystroke: the DOM already reflects it via native
    // contenteditable typing, so skip the regenerate-innerHTML-and-restore-cursor
    // dance — running it on every keystroke let a fast-typed next character land
    // between the regenerate and the cursor restore and get dropped (2026-09-02).
    // Matched by VALUE, not a flag the effect must clear: if the parent
    // normalizes the input back to an unchanged `value`, this effect never runs,
    // and a flag would stay stuck and swallow every later keystroke
    // (PR #3212 follow-up, fix 3). Consumed on every value change, so a parent
    // that stored a different string can't leave it armed for a later value.
    const isEcho = lastEmittedRef.current !== null && value === lastEmittedRef.current;
    lastEmittedRef.current = null;
    if (isEcho) return;
    if (editorRef.current) {
      // Check if cursor is currently inside a macro span - if so, skip re-render
      const sel = window.getSelection();
      let isInsideMacro = false;
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const startContainer = range.startContainer;
        if (startContainer.nodeType === Node.TEXT_NODE) {
          isInsideMacro = !!startContainer.parentElement?.closest('.macro');
        } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
          isInsideMacro = !!(startContainer as HTMLElement).closest('.macro');
        }
      }

      // Skip re-render if we're editing inside a macro span
      if (isInsideMacro || isEditingMacroRef.current) {
        return;
      }

      const currentPlainText = extractPlainText(editorRef.current);
      const valueChanged = currentPlainText !== value;

      // Check if suggestions changed (compare length and names)
      const suggestionsChanged =
        prevSuggestionsRef.current.length !== suggestions.length ||
        !prevSuggestionsRef.current.every((prev, idx) => prev.name === suggestions[idx]?.name);

      const currentHTML = editorRef.current.innerHTML;
      const parsedContentChanged = currentHTML !== parsedContent;

      // Re-render if value changed OR if suggestions changed (to re-validate macros)
      // OR if parsedContent changed (handles cases where suggestions reference changes but names are same)
      if (valueChanged || (suggestionsChanged && currentPlainText) || parsedContentChanged) {
        // Check if cursor is at a newline position skip re-render to preserve natural cursor movement
        let skipRerender = false;
        if (multiline && sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          const container = range.startContainer;

          if (container.nodeType === Node.TEXT_NODE) {
            const textNode = container as Text;
            if (range.startOffset === 0) {
              const prevSibling = textNode.previousSibling;
              if (prevSibling && (prevSibling as HTMLElement).tagName === 'BR') {
                skipRerender = true;
              }
            }
          } else if (container.nodeType === Node.ELEMENT_NODE) {
            const el = container as HTMLElement;
            if (['DIV', 'P'].includes(el.tagName) && range.startOffset === 0) {
              skipRerender = true;
            }
          }
        }

        if (!skipRerender) {
          // Only save/restore cursor when this editor has focus. Without this guard,
          // re-renders triggered by external changes (e.g. editing samples in the
          // left panel updating macro suggestions) steal focus via addRange.
          const editorHasFocus = editorRef.current.contains(document.activeElement);
          const savedCursorPos = editorHasFocus ? getCursorPosition(editorRef.current) : null;

          editorRef.current.innerHTML = parsedContent;
          // Always update ref to keep it in sync with current suggestions
          prevSuggestionsRef.current = suggestions;

          if (savedCursorPos !== null) {
            const sel = window.getSelection();
            if (sel) {
              try {
                const range = buildRangeFromOffsets(
                  editorRef.current,
                  savedCursorPos,
                  savedCursorPos,
                );
                sel.removeAllRanges();
                sel.addRange(range);
              } catch (e) {
                // If cursor position is out of bounds, place at end
                const range = document.createRange();
                range.selectNodeContents(editorRef.current);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
              }
            }
          }
        }
      }
    }
  }, [value, suggestions, parsedContent]);

  const handleCloseSuggestions = useCallback(() => {
    setShowSuggestions(false);
    setTriggerPosition(-1);
    setSearchText('');
    setEditingMacro(null);
  }, []);

  // Minimal DOM-input handler (detect macro-in-progress, wrap completed macros)
  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || isUpdatingRef.current || isComposingRef.current) return;

    isUpdatingRef.current = true;
    const emit = (text: string) => {
      lastEmittedRef.current = text;
      onChange(text);
    };

    try {
      // 1) Emit current plain text value to parent
      const plain = extractPlainText(editor);
      emit(plain);

      // Check if cursor is inside a macro span - if so, re-validate it
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const macroSpan = findMacroSpanAtCursor(range);

        if (macroSpan) {
          const currentText = macroSpan.textContent || '';

          // Check if the macro span contains text after a closing brace
          const closingBraceIndex = currentText.indexOf('}');
          if (closingBraceIndex >= 0 && closingBraceIndex < currentText.length - 1) {
            // There's text after the closing brace - we need to split it out
            const macroPart = currentText.slice(0, closingBraceIndex + 1);
            const extraText = currentText.slice(closingBraceIndex + 1);

            const macroValid = isValidMacro(macroPart, suggestions);

            // Update the macro span to only contain the macro part
            macroSpan.className = macroValid ? 'macro macro-valid' : 'macro macro-invalid';
            macroSpan.setAttribute('data-macro', macroPart);
            macroSpan.setAttribute('data-valid', String(macroValid));
            macroSpan.textContent = macroPart;

            // Create a text node for the extra text and insert it after the macro span
            const extraTextNode = document.createTextNode(extraText);
            macroSpan.parentNode?.insertBefore(extraTextNode, macroSpan.nextSibling);

            const parent = macroSpan.parentNode;
            if (parent) {
              parent.normalize();
            }

            const newRange = document.createRange();
            newRange.setStart(extraTextNode, extraText.length);
            newRange.collapse(true);
            if (sel) {
              sel.removeAllRanges();
              sel.addRange(newRange);
            }

            emit(extractPlainText(editor));
            return;
          }

          // Normal case: validate the macro (no text after closing brace)
          const valid = isValidMacro(currentText, suggestions);

          macroSpan.className = valid ? 'macro macro-valid' : 'macro macro-invalid';
          macroSpan.setAttribute('data-macro', currentText);
          macroSpan.setAttribute('data-valid', String(valid));
          return;
        }
      }

      // 2) Check if cursor is right after a completed macro (e.g., "{macro.name}|")
      const cursorPos = getCursorPosition(editor);
      if (cursorPos !== null && cursorPos > 0 && plain[cursorPos - 1] === '}') {
        // Scan backwards to find the opening brace
        let openIndex = -1;
        for (let i = cursorPos - 2; i >= 0; i--) {
          if (plain[i] === '{') {
            openIndex = i;
            break;
          }
          // Stop if we hit another closing brace (nested macros not supported)
          if (plain[i] === '}') {
            break;
          }
        }

        // If we found macro pattern, wrap it
        if (openIndex !== -1) {
          const fullMacroText = plain.slice(openIndex, cursorPos);
          const valid = isValidMacro(fullMacroText, suggestions);
          const fullRange = buildRangeFromOffsets(editor, openIndex, cursorPos);

          wrapRangeWithMacroSpan(fullRange, fullMacroText, valid);
          emit(extractPlainText(editor));
          handleCloseSuggestions();

          return;
        }
      }

      // 3) Find macro under cursor (if any)
      const macroRangeResult = findMacroRangeAtCursor(editor);
      if (macroRangeResult) {
        const { openIndex, cursorIndex } = macroRangeResult;

        // In JSON mode, ignore the outermost opening brace
        if (isJson) {
          const beforeBrace = plain.slice(0, openIndex).trim();
          if (beforeBrace.length === 0) {
            // This is the outermost brace in JSON, ignore it
            setShowSuggestions(false);
            setTriggerPosition(-1);
            setEditingMacro(null);
            return;
          }
        }

        // text between openIndex and cursorIndex (not including '{')
        const macroText = plain.slice(openIndex + 1, cursorIndex);

        // Still inside an open macro (no closing brace yet)
        setSearchText(macroText);
        setShowSuggestions(true);
        setTriggerPosition(openIndex);
        setSelectedIndex(0);

        return;
      }

      // No macro under cursor: hide suggestions
      setShowSuggestions(false);
      setTriggerPosition(-1);
      setEditingMacro(null);
    } finally {
      // Synchronous re-entrancy guard only — echo detection is lastEmittedRef's job.
      isUpdatingRef.current = false;
    }
  }, [onChange, suggestions, isJson, handleCloseSuggestions]);

  // Handle click to edit existing macro spans
  const handleClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const editor = editorRef.current;
    if (!editor) return;

    const macroEl = target.closest('.macro') as HTMLElement | null;
    if (macroEl) {
      e.preventDefault();
      e.stopPropagation();

      const macroText = macroEl.getAttribute('data-macro') || macroEl.textContent || '';

      // Put caret right after the clicked macro and open suggestions for editing
      const sel = window.getSelection();
      if (!sel) return;

      const range = document.createRange();
      range.setStartAfter(macroEl);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);

      setEditingMacro(macroText);
      setTriggerPosition(-1); // we'll derive trigger pos on next input if needed
      setShowSuggestions(true);
      setSearchText((macroText || '').replace(/[{}]/g, ''));
      setSelectedIndex(0);
    }
  }, []);

  // Insert macro selection (from dropdown)
  const handleInsertMacro = useCallback(
    (macro: MacroSuggestion) => {
      const editor = editorRef.current;
      if (!editor) return;

      isUpdatingRef.current = true;

      if (editingMacro) {
        // Replace clicked macro span
        const allMacros = editor.querySelectorAll('[data-macro]');
        const existing = Array.from(allMacros).find(
          (el) => el.getAttribute('data-macro') === editingMacro,
        ) as HTMLElement | null;
        if (existing) {
          const newSpan = document.createElement('span');
          const macroClassName = isValidMacro(macro.name, suggestions)
            ? 'macro macro-valid'
            : 'macro macro-invalid';
          newSpan.className = macroClassName;
          newSpan.setAttribute('data-macro', macro.name);
          newSpan.setAttribute('data-valid', String(isValidMacro(macro.name, suggestions)));
          newSpan.textContent = macro.name;
          newSpan.spellcheck = false;

          existing.replaceWith(newSpan);

          const parent = newSpan.parentNode;
          if (parent) {
            parent.normalize();
          }

          // caret after newSpan
          replaceRangeWithTextAndPlaceCursor(editor, newSpan, newSpan);

          onChange(extractPlainText(editor));
        }
      } else {
        // Replace in-progress macro range (triggered by typing '{' + content)
        // Use triggerPosition if available (where suggestions were opened),
        // otherwise fall back to findMacroRangeAtCursor
        let openIndex: number;
        let cursorIndex: number;

        if (triggerPosition >= 0) {
          // Use the position where suggestions were opened
          openIndex = triggerPosition;
          const currentCursorPos = getCursorPosition(editor);
          cursorIndex = currentCursorPos !== null ? currentCursorPos : openIndex + 1;
        } else {
          // Fallback: try to find macro range at cursor
          const macroRangeResult = findMacroRangeAtCursor(editor);
          if (!macroRangeResult) {
            // No macro range found, can't insert
            isUpdatingRef.current = false;
            return;
          }
          openIndex = macroRangeResult.openIndex;
          cursorIndex = macroRangeResult.cursorIndex;
        }

        // Build the full range to replace from openIndex to cursorIndex
        const fullRange = buildRangeFromOffsets(editor, openIndex, cursorIndex);

        const newSpan = document.createElement('span');
        const macroClassName = isValidMacro(macro.name, suggestions)
          ? 'macro macro-valid'
          : 'macro macro-invalid';
        newSpan.className = macroClassName;
        newSpan.setAttribute('data-macro', macro.name);
        newSpan.setAttribute('data-valid', String(isValidMacro(macro.name, suggestions)));
        newSpan.textContent = macro.name;
        newSpan.spellcheck = false;

        fullRange.deleteContents();
        fullRange.insertNode(newSpan);

        // Normalize the DOM to merge adjacent text nodes and remove empty nodes
        // This prevents empty text nodes from accumulating and breaking cursor navigation
        const parent = newSpan.parentNode;
        if (parent) {
          parent.normalize();
        }

        // place caret after span
        const sel = window.getSelection();
        if (sel) {
          const r = document.createRange();
          r.setStartAfter(newSpan);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        }

        onChange(extractPlainText(editor));
      }

      // cleanup
      setShowSuggestions(false);
      setTriggerPosition(-1);
      setSearchText('');
      setEditingMacro(null);

      isUpdatingRef.current = false;
      editor.focus();
    },
    [editingMacro, onChange, isJson, suggestions, triggerPosition],
  );

  // Insert a raw macro string (e.g. "{now+1}") at the trigger — used by a custom
  // macro popup (renderMacroPopup) instead of picking from the suggestion list.
  const insertRaw = useCallback(
    (raw: string) => {
      handleInsertMacro({ name: raw, display_name: raw, type: 'def', type_display: '' });
    },
    [handleInsertMacro],
  );

  // Keydown: navigation & selecting suggestions; let Enter be natural when suggestions closed
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // A custom popup (MacroBuilder) handles its own interaction; don't hijack keys.
      if (renderMacroPopup) {
        if (e.key === 'Escape') {
          e.preventDefault();
          handleCloseSuggestions();
        }
        return;
      }
      if (!showSuggestions || filteredSuggestions.length === 0) return;

      const total = filteredSuggestions.length;

      switch (e.key) {
        case 'Enter':
          if (showSuggestions && filteredSuggestions.length > 0) {
            e.preventDefault();
            handleInsertMacro(filteredSuggestions[selectedIndex]);
          }
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, total - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Tab':
          e.preventDefault();
          handleInsertMacro(filteredSuggestions[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          handleCloseSuggestions();
          break;
      }
    },
    [
      showSuggestions,
      filteredSuggestions,
      selectedIndex,
      handleInsertMacro,
      handleCloseSuggestions,
      renderMacroPopup,
    ],
  );

  // Paste: keep plain text and then run handleInput
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();

      const text = e.clipboardData.getData('text/plain');

      const sel = window.getSelection();
      if (!sel || !sel.rangeCount) return;

      const range = sel.getRangeAt(0);
      range.deleteContents();

      const node = document.createTextNode(text);
      range.insertNode(node);

      // place cursor after node
      range.setStartAfter(node);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);

      handleInput();
    },
    [handleInput],
  );

  const handleCopy = useCallback((e: React.ClipboardEvent) => {
    const editor = editorRef.current;
    if (!editor) return;

    const plain = extractPlainText(editor);

    e.preventDefault();
    e.clipboardData.setData('text/plain', plain);
  }, []);

  return (
    <Box className={styles.container}>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onPaste={handlePaste}
        onCopy={handleCopy}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          handleInput();
        }}
        spellCheck={false}
        className={`${styles.editor} ${multiline ? styles.multiLine : styles.singleLine} ${
          !value && !(clearPlaceholderOnFocus && focused) ? styles.empty : ''
        }`}
        data-placeholder={placeholder}
        style={{
          minHeight: multiline ? `${rows * 24}px` : 'auto',
          maxHeight: multiline && maxRows ? `${maxRows * 24}px` : multiline ? 'none' : 'auto',
          overflowY: multiline && maxRows ? 'auto' : 'hidden',
          ...sx,
        }}
      />
      {renderMacroPopup ? (
        <Popper
          open={showSuggestions}
          anchorEl={editorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
        >
          <ClickAwayListener
            onClickAway={handleCloseSuggestions}
            mouseEvent="onMouseDown"
            // MUI Select menus portal to <body>; ignore those so picking a unit/
            // format option in the builder doesn't dismiss the popup.
          >
            <Paper elevation={8}>
              {renderMacroPopup({ insert: insertRaw, close: handleCloseSuggestions })}
            </Paper>
          </ClickAwayListener>
        </Popper>
      ) : (
        <MacroSuggestions
          open={showSuggestions}
          anchorEl={editorRef.current}
          suggestions={filteredSuggestions}
          selectedIndex={selectedIndex}
          onSelect={(m) => handleInsertMacro(m)}
          onClose={handleCloseSuggestions}
          urlTemplate={value}
          renderFooter={renderSuggestionFooter}
        />
      )}
    </Box>
  );
};
